import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  computed,
  signal,
  inject,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { PostureRiskCheck, ItdrService } from '../../../../services/identity-security.service';
import { DatatableComponent } from 'src/app/core/components/datatable/datatable.component';
import { AcceptReasonDialogComponent } from '../accept-reason-dialog/accept-reason-dialog.component';

export interface EntityStateChange {
  checkId: string;
  entityUid: string;
  accepted: boolean;
  /** Audit note captured at acceptance time (absent on reopen). */
  reason?: string;
}

type AcceptTarget =
  | { kind: 'entity'; entityUid: string }
  | { kind: 'all' }
  | { kind: 'tenant' };

/** Minimal HTML escaping for values injected into DataTables render() strings. */
function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

@Component({
  selector: 'app-risk-check-detail',
  standalone: true,
  imports: [CommonModule, DatatableComponent, AcceptReasonDialogComponent],
  templateUrl: './risk-check-detail.component.html',
  styleUrl: './risk-check-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RiskCheckDetailComponent implements OnInit, AfterViewInit, OnDestroy {
  private itdr = inject(ItdrService);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);

  riskCheck = input.required<PostureRiskCheck>();
  organizationId = input.required<number>();
  close = output<void>();
  entityAction = output<EntityStateChange>();
  allAccepted = output<string>();
  checkReopened = output<string>();

  @ViewChild(DatatableComponent) private usersTable?: DatatableComponent;

  /** A check we could not evaluate has nothing to act on: no entities, no setting to accept. */
  protected notEvaluated = computed(() => this.riskCheck().status === 'insufficient_data');

  /** A passing check has no failing entity and no setting to accept -- its affected-users
   *  table would come back empty and an "Accept Risk" button would act on nothing. */
  protected passing = computed(() => this.riskCheck().status === 'pass');

  /** Read-only when there is nothing to act on: passing, or not evaluated. Hides the
   *  accept/reopen controls and the affected-users table. The informational blocks
   *  (current settings, Why This Matters, footer) still render. */
  protected readOnly = computed(() => this.notEvaluated() || this.passing());

  /** Tenant-scoped (config) checks have no entity list; they accept/reopen at the check level. */
  protected isTenantScope = computed(() => this.riskCheck().scope === 'tenant');
  protected tenantAccepted = signal<boolean>(false);
  protected tenantReason = signal<string>('');

  // Counts driven by the affected-users endpoint response (via successCallback).
  protected totalCount = signal<number>(0);
  protected acceptedCount = signal<number>(0);
  protected openCount = signal<number>(0);

  // Accept-reason dialog state.
  protected acceptTarget = signal<AcceptTarget | null>(null);
  protected acceptDialogTitle = computed(() => {
    const t = this.acceptTarget();
    if (t?.kind === 'all') return 'Accept all open risks';
    if (t?.kind === 'tenant') return 'Accept this tenant-wide risk';
    return 'Accept this risk';
  });

  /** URL the datatable pages against for this check's affected identities. */
  protected usersUrl = computed(() =>
    this.itdr.getAffectedUsersUrl(this.riskCheck().check_id, this.organizationId())
  );

  /** Aggregate config snapshot for the expanded card (empty when none provided). */
  protected currentSettings = computed(() => this.riskCheck().current_settings ?? []);

  protected userColumns = [
    {
      data: 'email',
      name: 'email',
      title: 'User',
      render: (d: string) => `<span class="fw-medium">${escapeHtml(d)}</span>`,
    },
    {
      data: 'status',
      name: 'status',
      title: 'Status',
      render: (d: string) =>
        d === 'Accepted'
          ? '<span class="severity-badge severity-neutral">Accepted</span>'
          : '<span class="badge bg-light text-dark border">Open</span>',
    },
    {
      data: 'reason',
      name: 'reason',
      title: 'Reason',
      orderable: false,
      render: (d: string) =>
        d
          ? `<span class="text-muted small">${escapeHtml(d)}</span>`
          : '<span class="text-muted">&mdash;</span>',
    },
    {
      data: null,
      title: '',
      orderable: false,
      // Keyed on entity_uid, not the address in the row: two entities can share a
      // display name, and an address can change hands.
      render: (_d: unknown, _t: unknown, row: { entity_uid: string; status: string }) =>
        row.status === 'Accepted'
          ? `<button type="button" class="btn btn-outline-danger btn-sm" data-reopen-uid="${escapeHtml(row.entity_uid)}">Reopen</button>`
          : `<button type="button" class="btn btn-outline-secondary btn-sm" data-accept-uid="${escapeHtml(row.entity_uid)}">Accept Risk</button>`,
    },
  ];

  /** Datatable hands us the raw response; we use it to keep the header counts live. */
  protected onUsersLoaded = (resp: {
    total_count?: number;
    accepted_count?: number;
    open_count?: number;
  }): void => {
    if (resp && typeof resp.total_count === 'number') {
      this.totalCount.set(resp.total_count);
      this.acceptedCount.set(resp.accepted_count ?? 0);
      this.openCount.set(resp.open_count ?? 0);
    }
  };

  // Per-row Accept/Reopen buttons are DataTables-rendered HTML, so we catch their
  // clicks via one delegated listener on the host rather than Angular bindings.
  private onHostClick = (event: Event): void => {
    const el = event.target as HTMLElement;
    const acceptBtn = el.closest('[data-accept-uid]');
    if (acceptBtn) {
      event.preventDefault();
      event.stopPropagation();
      this.openAcceptDialog({
        kind: 'entity',
        entityUid: acceptBtn.getAttribute('data-accept-uid') ?? '',
      });
      return;
    }
    const reopenBtn = el.closest('[data-reopen-uid]');
    if (reopenBtn) {
      event.preventDefault();
      event.stopPropagation();
      this.reopenEntity(reopenBtn.getAttribute('data-reopen-uid') ?? '');
    }
  };

  ngOnInit(): void {
    const check = this.riskCheck();
    const total = check.failed_entity_count;
    this.totalCount.set(total);
    if (check.state === 'Accepted') {
      this.tenantAccepted.set(true);
      this.acceptedCount.set(total);
      this.openCount.set(0);
    } else {
      this.openCount.set(total);
    }
  }

  ngAfterViewInit(): void {
    this.host.nativeElement.addEventListener('click', this.onHostClick);
  }

  ngOnDestroy(): void {
    this.host.nativeElement.removeEventListener('click', this.onHostClick);
  }

  protected onAcceptAll(): void {
    this.openAcceptDialog({ kind: 'all' });
  }

  protected onAcceptCheck(): void {
    this.openAcceptDialog({ kind: 'tenant' });
  }

  protected onReopenCheck(): void {
    this.itdr
      .setEntityAcceptance(this.riskCheck().check_id, this.organizationId(), '', false, '')
      .subscribe();
    this.tenantAccepted.set(false);
    this.tenantReason.set('');
    this.checkReopened.emit(this.riskCheck().check_id);
  }

  protected onAcceptCancel(): void {
    this.acceptTarget.set(null);
  }

  protected onAcceptConfirm(reason: string): void {
    const target = this.acceptTarget();
    this.acceptTarget.set(null);
    if (!target) return;
    const checkId = this.riskCheck().check_id;

    const organizationId = this.organizationId();

    // A tenant-scoped check has no identities, so it is accepted as a whole: an
    // empty uid. This used to update local state only, which meant the decision
    // vanished on reload and never reached the score.
    if (target.kind === 'tenant') {
      this.itdr
        .setEntityAcceptance(checkId, organizationId, '', true, reason)
        .subscribe(() => {
          this.tenantAccepted.set(true);
          this.tenantReason.set(reason || 'Accepted without justification');
          this.allAccepted.emit(checkId);
        });
      return;
    }

    if (target.kind === 'all') {
      this.itdr
        .setEntityAcceptance(checkId, organizationId, '*', true, reason)
        .subscribe(() => {
          this.usersTable?.reload();
          this.allAccepted.emit(checkId);
        });
      return;
    }

    this.itdr
      .setEntityAcceptance(checkId, organizationId, target.entityUid, true, reason)
      .subscribe(() => {
        this.usersTable?.reload();
        this.entityAction.emit({
          checkId,
          entityUid: target.entityUid,
          accepted: true,
          reason,
        });
      });
  }

  private openAcceptDialog(target: AcceptTarget): void {
    this.acceptTarget.set(target);
  }

  private reopenEntity(entityUid: string): void {
    const checkId = this.riskCheck().check_id;
    const wasFullyAccepted = this.openCount() === 0 && this.totalCount() > 0;
    this.itdr
      .setEntityAcceptance(checkId, this.organizationId(), entityUid, false, '')
      .subscribe(() => {
        this.usersTable?.reload();
        this.entityAction.emit({ checkId, entityUid, accepted: false });
        if (wasFullyAccepted) {
          this.checkReopened.emit(checkId);
        }
      });
  }
}
